import { checkIsBrowser } from '@/utils/functions/checkServerEnvironment';
import axios, { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import debug from 'debug';
import { anyPass, filter, isEmpty, isNil, omit, pick, reduce, toPairs } from 'ramda';

import { EN_REQUEST_ERRORS, TRequesterResponse } from '../IRequester';
import { Requester } from '../Requester';

const log = debug('sunny:AxiosRequester');
export type THTTPMethods = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'HEAD';

const isEmptyHeader = anyPass([isEmpty, isNil]);

const validationHeaders = (headers: IAxiosRequesterConfig['headers']): Record<string, string> => {
  return reduce(
    (result: Record<string, string>, item: any) => {
      const key: string = item[0];
      const value: string = item[1];

      if (!key || !value) {
        return result;
      }

      result[key] = value;
      return result;
    },
    {},
    filter((keyValue) => !isEmptyHeader(keyValue[1]), toPairs(headers)),
  );
};

export interface IAxiosRequesterConfig extends AxiosRequestConfig {
  method: Method;
  url: string;
}

export class AxiosRequester extends Requester<IAxiosRequesterConfig> {
  constructor(public id: string) {
    super();
  }

  async call<P>(config: IAxiosRequesterConfig): Promise<TRequesterResponse<P>> {
    this.onStart();
    try {
      log('call', config.method, this.id);
      const isBrowser = checkIsBrowser();
      if (isBrowser) {
        config.headers = {
          ...config.headers,
          'X-Requested-With': 'XMLHttpRequest',
        };
      } else {
        config.headers = {
          ...omit(['host', 'Host'])(config.headers),
        };
      }

      config.headers = validationHeaders(config.headers);

      const result: AxiosResponse<P> = await axios(config);

      return {
        success: true,
        statusCode: result.status,
        data: result.data,
        headers: result.headers,
        config,
        duration: this.getDuration(new Date()),
      };
    } catch (error) {
      const duration = this.getDuration(new Date());

      // 서버로부터 에러가 내려온 경우
      if (error.response) {
        const errResponse = error.response;

        return {
          success: false,
          statusCode: errResponse.status,
          errorType: EN_REQUEST_ERRORS.AXIOS,
          config: pick(['url', 'method', 'headers', 'params', 'data', 'timeout'], error.config),
          data: errResponse.data,
          duration,
        };
      }

      // Axios timeout 시, 보다 더 적극적인 운영 대응을 위한 커스텀 응답
      if (error.code === 'ECONNABORTED') {
        return {
          success: false,
          statusCode: 504,
          errorType: EN_REQUEST_ERRORS.AXIOS,
          config: pick(['url', 'method', 'headers', 'params', 'data', 'timeout'], error.config),
          data: error.request.statusText || 'Response Timeout',
          duration,
        };
      }

      // Client 요청 에러 (XMLHttpRequest Error)
      if (error.request) {
        return {
          success: false,
          statusCode: 400,
          errorType: EN_REQUEST_ERRORS.CLIENT,
          config: pick(['url', 'method', 'headers', 'params', 'data', 'timeout'], error.config),
          data: error.request.statusText || 'Client Request Error',
          duration,
        };
      }

      // Unknown Error
      return {
        success: false,
        statusCode: 400,
        errorType: EN_REQUEST_ERRORS.UNKNOWN,
        config: pick(['url', 'method', 'headers', 'params', 'data', 'timeout'], error.config),
        data: error.message,
        duration,
      };
    } finally {
      this.onEnd();
    }
  }
}
